import { useFormik } from "formik";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import CreatableSelect from "react-select/creatable";
import Select from "react-select";
import DateTime from "react-datetime";
import FormBuilderModal from "./FormBuilderModal";
import { useState, useMemo, useRef, Fragment, useEffect } from "react";
import moment from "moment";
import { useAuth } from "../../hooks/useAuth";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import ModalLoader from "../utils/ModalLoader";
import {
  formDataForFormBuider,
  formTypes,
  pageSizes,
  personalFormTypes,
} from "../../utils/helpers";
import { isEmpty, uniqBy } from "lodash";
import { queryActions } from "../../utils/reactQueryActions";
import ConfirmDialog from "../ConfirmDialogue";
import { useEffectOnce, useSessionStorage } from "../../utils/hooks";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

export default function NewRequisitionFormBuilder({
  showNewRequisitionModal,
  setShowNewRequisitionModal,
  refetch,
  type,
  selectedForm,
  setSelectedForm,
}) {
  // FOR DEDUPLICATION OF DRAFTS: use a clientKey stored in sessionStorage to scope which draft belongs to which tab/window.
  const storageKey = `clientKey:${window.location.pathname}`;
  const [clientKey] = useSessionStorage(storageKey, uuidv4());

  const navigate = useNavigate();
  const location = useLocation();

  const queryClient = useQueryClient();
  const { backendUrl, token, user: authUser, isPersonalAccount } = useAuth();
  const [file, setFile] = useState();
  const [showFormBuilderModal, setShowFormBuilderModal] = useState(
    // IF AI OPENED THIS PAGE, SHOW THE FORM BUILDER MODAL
    location.state?.action === "naila-form-builder-create" || false
  );

  const setup = async () => {
    let response = await fetch(`${backendUrl}/api/forms/set-up`, {
      method: "GET",
      headers: {
        Accept: "Application/json",
        "Content-Type": "Application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();
    data.formCategory = data.formCategory
      .map(el => ({
        label: el.category,
        value: el.category,
      }))
      .filter(el => el.value); // filter out empty categories

    /* data.formType = data.formType.map((el) => ({
      label: el.type,
      value: el.type,
    })); */

    return data;
  };

  const {
    data: setUpData,
    // refetch,
  } = useQuery(["FORM_SETUP"], () => setup(), {
    keepPreviousData: true,
  });

  //----------------------------------------------------------------------
  // fetch data for form
  const getDepartmentsForForm = async () => {
    // await waitFor(5000);
    let response = await fetch(
      `${backendUrl}/api/users/departments-with-users`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();
    return data;
  };

  const {
    data = { departments: [], staffs: [], jobGrades: [] },
    isFetching,
  } = useQuery(["GET_USERS_WITH_DEPARTMENTS"], () => getDepartmentsForForm(), {
    enabled: true,
    keepPreviousData: true,
  });

  const createTemplate = async payload => {
    const formData = formDataForFormBuider(payload);

    let url =
      type === "Template"
        ? `${backendUrl}/api/forms/create`
        : `${backendUrl}/api/forms/create-form`;

    let response = await fetch(url, {
      method: "POST",
      credentials: "include",
      body: formData,
      headers: {
        "x-client-key": clientKey,
      },
    });
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    const res = await response.json();
    return res;
  };

  const createMutation = useMutation(payload => createTemplate(payload), {
    onSuccess: ({ message }) => {
      // Destroy autosave draft ref on real submit
      autosavedIdRef.current = null;
      // DESTROY ROUTE STATE
      if (location.state !== null) {
        navigate(location, { replace: true, state: null });
      }

      toast.success(message);
      formik.resetForm();
      refetch();

      setShowFormBuilderModal(false);
      setShowNewRequisitionModal(false);
    },
    onError: ({ message = "" }) => {
      toast.error(`Unable to perform action: ${message}`);
    },
  });

  // Tracks the ID of the autosave draft once one is created
  const autosavedIdRef = useRef(null);
  // Tracks whether a create-autosave request is in flight (ref so stale closures always see current value)
  const isAutoSaveCreatingRef = useRef(false);

  const editDraftTemplate = async payload => {
    const formData = formDataForFormBuider(payload);
    const url =
      type === "Template"
        ? `${backendUrl}/api/forms/edit`
        : `${backendUrl}/api/forms/edit-form`;
    let response = await fetch(url, {
      method: "POST",
      credentials: "include",
      body: formData,
    });
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    return response.json();
  };

  // First autosave → create draft, store returned ID
  const createAutoSaveMutation = useMutation(
    payload => createTemplate(payload),
    {
      onSuccess: ({ data }) => {
        if (data?.id) autosavedIdRef.current = data.id;
        isAutoSaveCreatingRef.current = false;
      },
      onError: () => {
        isAutoSaveCreatingRef.current = false;
        toast.error("Autosave failed");
      },
    }
  );

  // Subsequent autosaves → edit the draft
  const editAutoSaveMutation = useMutation(
    payload => editDraftTemplate(payload),
    {
      onError: () => toast.error("Autosave failed"),
    }
  );

  // Final submit when autosave already created a draft → edit, don't create a duplicate
  const editDraftFinalMutation = useMutation(
    payload => editDraftTemplate(payload),
    {
      onSuccess: ({ message }) => {
        autosavedIdRef.current = null;
        if (location.state !== null) {
          navigate(location, { replace: true, state: null });
        }
        toast.success(message);
        formik.resetForm();
        refetch();
        setShowFormBuilderModal(false);
        setShowNewRequisitionModal(false);
      },
      onError: ({ message = "" }) => {
        toast.error(`Unable to perform action: ${message}`);
      },
    }
  );

  const formik = useFormik({
    initialValues: {
      Title: "",
      Date_Log: moment(),
      Type: "Organisation",
      Category: "",
      Template: "",
      Draft: false,
      // Signatories: [],
      // approvalType: "Required",
      // approvalType: "Has Approval",
      approvalType: "Not-Required",
      department: "",
      jobGrade: "",
      staff: "",
      enabled: true,
      formStartNumber: "",
      formEndNumber: "",
      formNumberPrefix: "",
      revision: 0,
      orientation: "Potrait",
      pageSize: "A4",
      register: false,
      numberType: "Increment",
      documentNumber: "",
      isMultiStep: false,
    },
    onSubmit: values => {
      if (autosavedIdRef.current) {
        // Draft already exists — update it to a real (non-autosave) submission
        editDraftFinalMutation.mutate({
          ...values,
          id: autosavedIdRef.current,
        });
      } else {
        createMutation.mutate({ ...values });
      }
    },
  });

  // Once
  useEffectOnce(() => {
    //
    if (!isEmpty(selectedForm)) {
      formik.setValues({
        ...formik.values,
        Template: selectedForm.DraftTemplate
          ? JSON.parse(selectedForm.DraftTemplate)
          : JSON.parse(selectedForm.Template),
        Type: selectedForm.Type,
        Category: selectedForm.Category,
        approvalType: selectedForm.approvalType,
        department: selectedForm.department,
        jobGrade: selectedForm.jobGrade,
        staff: selectedForm.staff,
      });
    }
  });

  useEffect(() => {
    //
    if (location.state?.action === "naila-form-builder-create") {
      // handle the action
      formik.setValues({
        ...formik.values,
        Title: location.state?.payload?.title,
        Template: location.state?.payload?.pages,
        isMultiStep: location.state?.payload?.isMultiStep,
      });

      //
      setShowFormBuilderModal(true);
    }
  }, [location.state?.timestamp]);

  //
  const saveForm = async ({ data, isDraft, isMultiStep, autoSave = false }) => {
    // IF AI OPENED THIS
    // THE USER NEEDS TO ADD OTHER FIELDS
    if (location.state?.action === "naila-form-builder-create") {
      setShowFormBuilderModal(false);
      return;
    }

    if (autoSave) {
      formik.setFieldValue("Template", data);
      if (autosavedIdRef.current) {
        editAutoSaveMutation.mutate({
          ...formik.values,
          Template: data,
          isMultiStep,
          id: autosavedIdRef.current,
        });
      } else if (!isAutoSaveCreatingRef.current) {
        // Guard: skip if a create is already in flight (use ref so stale closures always see current value)
        isAutoSaveCreatingRef.current = true;
        createAutoSaveMutation.mutate({
          ...formik.values,
          Template: data,
          isMultiStep,
          autosave: true,
        });
      }
      return;
    }

    //
    formik.setFieldValue("Template", data);
    formik.setFieldValue("Draft", isDraft);
    formik.setFieldValue("isMultiStep", isMultiStep);

    if (
      await ConfirmDialog({
        title: "Submit",
        description: "Are you sure, you want to submit",
      })
    ) {
      formik.submitForm();
    }
  };

  const handleCreateType = newType => {
    queryClient.setQueryData(["FORM_SETUP"], data => {
      data.formType = [...data.formType, { label: newType, value: newType }];
      return data;
    });
    formik.setFieldValue("Type", newType);
  };

  const handleCreateCategory = newCategory => {
    queryClient.setQueryData(["FORM_SETUP"], data => {
      data.formCategory = [
        ...data.formCategory,
        { label: newCategory, value: newCategory },
      ];
      return data;
    });
    formik.setFieldValue("Category", newCategory);
  };

  const handleFileInput = e => {
    e.preventDefault();
    let fileInput = e.target;
    setFile(fileInput.files[0]);
  };

  const staffSelectOptions = useMemo(() => {
    const jobGrades = data.staffs
      .filter(el => el.Department === formik.values.department)
      .map(el => ({ jobGrade: el.jobGrade }));

    const staffs = data.staffs.filter(el => {
      if (!formik.values.department) return true;

      if (!formik.values.department && !formik.values.jobGrade) return true;

      if (formik.values.department === "Department Lead") {
        const leads = data.departmentWithLeads
          .filter(el => el.Dept_Lead)
          .map(el => JSON.parse(el.Dept_Lead))
          .flat();

        return leads.some(lead => lead.Staff_ID === el.Staff_ID);
      }

      if (formik.values.department && !formik.values.jobGrade) {
        return el.Department === formik.values.department;
      }

      if (formik.values.department && formik.values.jobGrade) {
        return (
          el.Department === formik.values.department &&
          el.jobGrade === formik.values.jobGrade
        );
      }
    });

    return {
      departments: [
        { label: "Any Department", value: "" },
        { label: "Department Lead", value: "Department Lead" },
        ...data.departments.map(el => ({
          label: el.Department,
          value: el.Department,
        })),
      ],
      jobGrades: [
        { label: "Any Job Grade", value: "" },
        ...uniqBy(jobGrades, "jobGrade").map(el => ({
          label: el.jobGrade,
          value: el.jobGrade,
        })),
      ],
      staffs: [
        { label: "Any Staff", value: "" },
        ...staffs.map(el => ({
          label: `${el?.changeableId ? el?.changeableId : el.Staff_ID} - ${
            el.Name
          }`,
          value: el.Staff_ID,
        })),
      ],
    };
  }, [data, formik.values.department, formik.values.jobGrade]);

  //
  const resolvedFormTypes = useMemo(() => {
    return isPersonalAccount ? personalFormTypes : formTypes;
  }, [isPersonalAccount]);

  return (
    <>
      <Modal
        show={showNewRequisitionModal}
        backdrop="static"
        animation={false}
        onHide={() => {
          // DESTROY ROUTE STATE
          if (location.state !== null) {
            navigate(location, { replace: true, state: null });
          }

          setShowNewRequisitionModal(false);
          if (setSelectedForm) setSelectedForm(null);
        }}
      >
        <Modal.Header closeButton>
          <div>
            <h1 className="h5">Create New {type}</h1>{" "}
            <p>Build your forms correctly and as needed</p>
          </div>
        </Modal.Header>

        <Modal.Body>
          <Form noValidate onSubmit={formik.handleSubmit}>
            {type !== "Template" && (
              <>
                <Form.Group className=" mb-3 pb-2">
                  {/* RENAMED FROM Document Number to Form Number*/}
                  <Form.Label className="mb-1">Form Number</Form.Label>
                  <Form.Control
                    className=""
                    type="text"
                    placeholder="Enter Form Number"
                    name="documentNumber"
                    value={formik.values.documentNumber}
                    onChange={formik.handleChange}
                    isInvalid={
                      formik.touched.documentNumber &&
                      !!formik.errors.documentNumber
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.documentNumber}
                  </Form.Control.Feedback>
                </Form.Group>

                <div className="d-flex gap-2">
                  <Form.Group className=" mb-3 pb-2">
                    <Form.Label className="mb-1">Document Number</Form.Label>
                    <Form.Control
                      className=""
                      type="text"
                      placeholder="eg. AB-BC-CD"
                      name="formNumberPrefix"
                      value={formik.values.formNumberPrefix}
                      onChange={formik.handleChange}
                      isInvalid={
                        formik.touched.formNumberPrefix &&
                        !!formik.errors.formNumberPrefix
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.formNumberPrefix}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className=" mb-3 pb-2">
                    <Form.Label className="mb-1">Start Number</Form.Label>
                    <Form.Control
                      className=""
                      type="number"
                      placeholder="Enter Start Number"
                      name="formStartNumber"
                      value={formik.values.formStartNumber}
                      onChange={formik.handleChange}
                      isInvalid={
                        formik.touched.formStartNumber &&
                        !!formik.errors.formStartNumber
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.formStartNumber}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className=" mb-3 pb-2">
                    <Form.Label className="mb-1">End Number</Form.Label>
                    <Form.Control
                      className=""
                      type="number"
                      placeholder="Enter Start Number"
                      name="formEndNumber"
                      value={formik.values.formEndNumber}
                      onChange={formik.handleChange}
                      isInvalid={
                        formik.touched.formEndNumber &&
                        !!formik.errors.formEndNumber
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.formEndNumber}
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>Document Number Type</Form.Label>

                  <div>
                    <Form.Check
                      inline
                      label="Increment"
                      value="Increment"
                      name="numberType"
                      type={"radio"}
                      id="Increment"
                      checked={formik.values.numberType === "Increment"}
                      onChange={formik.handleChange}
                    />
                    <Form.Check
                      inline
                      label="Fixed"
                      value="Fixed"
                      name="numberType"
                      type={"radio"}
                      id="Fixed"
                      checked={formik.values.numberType === "Fixed"}
                      onChange={formik.handleChange}
                    />
                  </div>
                </Form.Group>
              </>
            )}

            <Form.Group className=" mb-3 pb-2">
              <Form.Label className="mb-1">Title</Form.Label>
              <Form.Control
                className=""
                type="text"
                placeholder="Enter Title"
                name="Title"
                value={formik.values.Title}
                onChange={formik.handleChange}
                isInvalid={formik.touched.Title && !!formik.errors.Title}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.Title}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className=" mb-3 pb-2">
              <Form.Label className="mb-1">Type</Form.Label>
              <CreatableSelect
                classNamePrefix={`form-select`}
                options={resolvedFormTypes}
                value={resolvedFormTypes.find(
                  el => el.value === formik.values.Type
                )}
                onChange={({ value }) => {
                  formik.setFieldValue("Type", value);
                  if (value === "Public") {
                    formik.setFieldValue("approvalType", "Not-Required");
                  }
                }}
                isSearchable
                onCreateOption={handleCreateType}
              />
              <Form.Control.Feedback type="invalid" />
            </Form.Group>

            {/* PERSONAL SHOULD NOT SEE PEOPLE  */}
            {formik.values.Type === "Organisation" && !isPersonalAccount ? (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Department </Form.Label>
                  <Select
                    classNamePrefix={`form-select`}
                    name="department"
                    options={staffSelectOptions?.departments}
                    value={staffSelectOptions?.departments.find(
                      el => el.value === formik.values.department
                    )}
                    onChange={({ value }) =>
                      formik.setFieldValue("department", value)
                    }
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Job Grade </Form.Label>

                  <Select
                    classNamePrefix={`form-select`}
                    name="jobGrade"
                    options={staffSelectOptions?.jobGrades}
                    value={staffSelectOptions?.jobGrades.find(
                      el => el.value === formik.values.jobGrade
                    )}
                    onChange={({ value }) =>
                      formik.setFieldValue("jobGrade", value)
                    }
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Staff </Form.Label>
                  <Select
                    classNamePrefix={`form-select`}
                    name="staff"
                    options={staffSelectOptions?.staffs}
                    value={staffSelectOptions?.staffs.find(
                      el => el.value === formik.values.staff
                    )}
                    onChange={({ value }) =>
                      formik.setFieldValue("staff", value)
                    }
                  />
                </Form.Group>
              </>
            ) : null}

            <Form.Group className=" mb-3 pb-2">
              <Form.Label className="mb-1">Category</Form.Label>
              <CreatableSelect
                classNamePrefix={`form-select`}
                options={setUpData?.formCategory}
                value={setUpData?.formCategory.find(
                  el => el.value === formik.values.Category
                )}
                onChange={({ value }) =>
                  formik.setFieldValue("Category", value)
                }
                isSearchable
                onCreateOption={handleCreateCategory}
                placeholder="Select or create category"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Has Approval</Form.Label>

              <div>
                <Form.Check
                  inline
                  label="Approval Required"
                  value="Required"
                  name="approvalType"
                  type={"radio"}
                  id="Approval Required"
                  checked={formik.values.approvalType === "Required"}
                  onChange={formik.handleChange}
                />
                <Form.Check
                  inline
                  label="Fill form only (No Approval Required)"
                  value="Not-Required"
                  name="approvalType"
                  type={"radio"}
                  id="Fill form only"
                  checked={formik.values.approvalType === "Not-Required"}
                  onChange={formik.handleChange}
                />

                <Form.Check
                  inline
                  label="Fill form and Send for Approval"
                  value="Fill and Approval Required"
                  name="approvalType"
                  type={"radio"}
                  id="Fill form only"
                  checked={
                    formik.values.approvalType === "Fill and Approval Required"
                  }
                  onChange={formik.handleChange}
                />
              </div>
            </Form.Group>

            <Row>
              <Col>
                <Form.Group className="mb-3 pb-2">
                  <Form.Label className="mb-1">Date</Form.Label>
                  <DateTime
                    timeFormat={false}
                    closeOnSelect={true}
                    closeOnClickOutside={true}
                    dateFormat="MMM DD, YYYY"
                    inputProps={{
                      className: `date-input form-control`,
                      placeholder: "Select date",
                      readOnly: true,
                    }}
                    value={formik.values.Date_Log}
                    onChange={date => {
                      formik.setFieldValue("Date_Log", date, true);
                    }}
                  />
                </Form.Group>
              </Col>

              <Col>
                <Form.Group className=" mb-3 pb-2">
                  <Form.Label className="mb-1">Revision</Form.Label>
                  <Form.Control
                    className=""
                    type="number"
                    placeholder="Enter Revision"
                    name="revision"
                    value={formik.values.revision}
                    onChange={formik.handleChange}
                    isInvalid={
                      formik.touched.revision && !!formik.errors.revision
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.revision}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Page Size</Form.Label>
                  <Select
                    classNamePrefix={"form-select"}
                    options={pageSizes}
                    value={pageSizes.find(
                      el => el.value === formik.values.pageSize
                    )}
                    onChange={({ value }) =>
                      formik.setFieldValue("pageSize", value)
                    }
                  />
                </Form.Group>
              </Col>

              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Orientation</Form.Label>

                  <div>
                    <Form.Check
                      inline
                      label="Potrait"
                      value="Potrait"
                      name="orientation"
                      type={"radio"}
                      id="Potrait"
                      checked={formik.values.orientation === "Potrait"}
                      onChange={formik.handleChange}
                    />
                    <Form.Check
                      inline
                      label="Landscape"
                      value="Landscape"
                      name="orientation"
                      type={"radio"}
                      id="Landscape"
                      checked={formik.values.orientation === "Landscape"}
                      onChange={formik.handleChange}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col>
                <Form.Group className=" mb-3 pb-2">
                  <Form.Label className="d-flex gap-3 fw-6">
                    <Form.Switch
                      label="Enabled "
                      checked={formik.values.enabled}
                      onChange={() =>
                        formik.setFieldValue("enabled", !formik.values.enabled)
                      }
                    />
                  </Form.Label>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className=" mb-3 pb-2">
                  <Form.Label className="d-flex gap-3 fw-6">
                    <Form.Switch
                      label="Register Form"
                      checked={formik.values.register}
                      onChange={() =>
                        formik.setFieldValue(
                          "register",
                          !formik.values.register
                        )
                      }
                    />
                  </Form.Label>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className=" mb-3 pb-2">
                  <Form.Label className="d-flex gap-3 fw-6">
                    <Form.Switch
                      label="Make multi-step form"
                      checked={formik.values.isMultiStep}
                      onChange={() =>
                        formik.setFieldValue(
                          "isMultiStep",
                          !formik.values.isMultiStep
                        )
                      }
                    />
                  </Form.Label>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          {/* ORIGINAL CONTEXT (DEFAULT) */}
          {!location.state?.action && (
            <Button
              onClick={() => {
                if (
                  isEmpty(formik.values.formNumberPrefix) &&
                  type !== "Template"
                ) {
                  toast.error("Enter Document Number");
                  return formik.setFieldError(
                    "formNumberPrefix",
                    "Enter Document Number"
                  );
                }

                return setShowFormBuilderModal(true);
              }}
              className="px-4"
            >
              Create Form
            </Button>
          )}

          {/* CONTEXT IF FORM BUILDER WAS OPEN FIRST  (AI) */}
          {location.state?.action === "naila-form-builder-create" && (
            <>
              <Button
                onClick={() => setShowFormBuilderModal(true)}
                className="px-4"
                variant="outline-primary"
              >
                Continue Editing
              </Button>

              <Button
                onClick={() => {
                  if (
                    isEmpty(formik.values.formNumberPrefix) &&
                    type !== "Template"
                  ) {
                    toast.error("Enter Document Number");
                    return formik.setFieldError(
                      "formNumberPrefix",
                      "Enter Document Number"
                    );
                  }
                  formik.submitForm();
                }}
                className="px-4"
              >
                Save and Publish
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {showFormBuilderModal && (
        <FormBuilderModal
          showFormBuilderModal={showFormBuilderModal}
          setShowFormBuilderModal={setShowFormBuilderModal}
          saveForm={saveForm}
          template={
            formik.values.Template
              ? JSON.stringify(formik.values.Template)
              : selectedForm?.Template ||
                JSON.stringify(location.state?.payload?.pages)
          }
          isMultiStep={formik.values.isMultiStep}
        />
      )}

      <ModalLoader show={createMutation.isLoading} />

      {(createAutoSaveMutation.isLoading ||
        editAutoSaveMutation.isLoading ||
        createAutoSaveMutation.isSuccess ||
        editAutoSaveMutation.isSuccess) && (
        <div
          style={{
            position: "fixed",
            bottom: "1.5rem",
            right: "1.5rem",
            zIndex: 9999,
          }}
          className="bg-white shadow rounded px-3 py-2 fw-semibold"
        >
          {createAutoSaveMutation.isLoading ||
          editAutoSaveMutation.isLoading ? (
            <i className="text-muted">Saving...</i>
          ) : (
            <span className="text-success">Saved ✅</span>
          )}
        </div>
      )}
    </>
  );
}
